/**
 * Base Grid Element Styles
 *
 * Core styles for the tbw-grid element including layout fundamentals,
 * box-sizing, and root element properties.
 *
 * @layer tbw-base
 */

@layer tbw-base {
  tbw-grid {
    /* Inherit color-scheme from document to respect page's dark/light mode setting.
       The host page should set `color-scheme: light`, `color-scheme: dark`, or
       `color-scheme: light dark` on :root or an ancestor element. */
    color-scheme: inherit;

    position: relative;
    display: block;
    width: 100%;
    height: 100%;
    min-height: 0;
    contain: content;
    font-family: var(--tbw-font-family);
    font-size: var(--tbw-font-size);
    font-feature-settings: 'tnum', 'lnum'; /* tabular-nums, lining-nums */
    background: var(--tbw-color-bg);
    color: var(--tbw-color-fg);
    border: 1px solid var(--tbw-color-border);
    border-radius: var(--tbw-border-radius);
    overflow: clip;
    outline: none;

    /* Box sizing for all descendants */
    &,
    & * {
      box-sizing: border-box;
    }

    /* Light-DOM configuration elements are parsed into grid config/templates
       and must never render their own raw markup (e.g. an inline
       `<tbw-grid-column-view>` `{{ }}` template would otherwise show literally
       above the grid). Only CORE-owned config tags are hidden here. Plugin-owned
       tags — `tbw-grid-detail` (master-detail) and the shell tags
       `tbw-grid-header` / `tbw-grid-tool-buttons` / `tbw-grid-tool-panel` — are
       intentionally left visible so that an un-imported plugin's leftover config
       is a visible "you forgot to import it" signal (see the light-DOM
       decoupling INVARIANT in grid-plugins-catalog). Hiding a config tag does
       not affect parsing or framework-adapter template registration (both read
       from `display:none` elements). */
    & > tbw-grid-column,
    & > tbw-grid-type {
      display: none;
    }

    /* ========== Grid Root Layout ========== */

    .tbw-grid-root {
      position: relative; /* For loading overlay positioning */
      display: flex;
      flex-direction: column;
      height: 100%;

      &.has-shell {
        display: flex;
        flex-direction: column;
        height: 100%;
      }
      &:has(.selected) {
        user-select: none;
      }
    }

    /* Core grid-content wrapper. Rendered by dom-builder's `buildGridDOM` for
       every grid (shell or not): without a shell it wraps `.tbw-scroll-area`
       directly; with a shell it sits inside `.tbw-shell-body` alongside the
       tool panel. Either way it must be a flex item that fills its parent so
       the `flex: 1` scroll-area below can claim the viewport height. This is
       core layout, NOT a shell concern — keeping it here ensures shell-less
       grids (the default since the shell became opt-in) don't collapse to a
       single row. Previously orphaned in shell.css. */
    .tbw-grid-content {
      flex: 1;
      min-width: 0;
      min-height: 0;
      display: flex;
      flex-direction: row;
      overflow: hidden;
      /* TOUCH-ACTION POLICY: The grid manages its own *scrolling* in JS (see
         touch-scroll.ts + setupTouchScrollListeners) rather than delegating to the
         browser compositor. Every element that would otherwise receive native touch
         scroll, pan, or zoom must therefore declare touch-action: none, so the
         compositor does not compete with or cancel the grid's handlers mid-gesture.
         This policy covers scroll surfaces and JS-driven drag affordances only —
         it is not a claim that every touch interaction in the grid is JS-managed.
         MUST NOT use: pan-x pan-y (delegates scroll to browser, breaks faux scrollbar)
         MUST NOT use: manipulation (suppresses double-tap-to-zoom, a WCAG violation).
         touch-action: none is the only correct value for JS-managed scroll elements. */
      touch-action: none;
    }

    /* Core scroll viewport. Rendered by dom-builder's gridContentTemplate for
       every grid, with or without the shell — so it is core layout, not a
       shell concern (the shell only wraps it in a `.tbw-grid-content` div).
       Its siblings (.rows-body-wrapper, .faux-vscroll, .tbw-sr-only) all live
       here too; this rule was previously orphaned in shell.css (#370). */
    .tbw-scroll-area {
      flex: 1;
      min-width: 0;
      min-height: 0;
      display: flex;
      flex-direction: column;
      overflow-x: auto;
      overflow-y: hidden;
      overflow-anchor: none;
    }

    .rows-body-wrapper {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: row;
      width: 100%;
      min-width: fit-content;
    }

    .rows-body {
      flex: 1;
      min-width: 0;
      min-height: 0;
      display: flex;
      flex-direction: column;
      overflow: visible;
    }

    .rows-container {
      display: flex;
      flex-direction: row;
      flex: 1;
      min-height: 0;
      overflow: visible;
      /* Positioning context for the empty-state overlay (default
         emptyOverlay='rows' target). Without this the overlay would
         resolve to .tbw-grid-root and cover the header — see #321. */
      position: relative;
    }

    .rows-viewport {
      flex: 1;
      min-width: 0;
      position: relative;
      display: block;
      overflow: clip;
      /* Grid handles touch scrolling via JS (faux scrollbar pattern).
         Prevent browser from competing with our touch handlers. */
      touch-action: none;

      .rows {
        position: absolute;
        top: 0;
        left: 0;
        min-width: 100%;
        will-change: transform;
        z-index: var(--tbw-z-layer-rows, 1);
      }
    }

    /* ========== Faux Scrollbar ========== */

    .faux-vscroll {
      position: sticky;
      inset-inline-end: 0;
      flex-shrink: 0;
      width: auto;
      overflow-y: auto;
      overflow-x: hidden;
      z-index: var(--tbw-z-layer-header, 30);
      /* Prevent native touch scrolling — JS touch handler manages scroll via faux scrollbar */
      touch-action: none;
    }

    .faux-vscroll-spacer {
      width: 1px;
    }

    /* ========== Screen Reader Only ========== */

    .tbw-sr-only {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }

    /* ========== Focus Styling ========== */

    &[data-has-focus] {
      .cell-focus,
      .row-focus {
        outline: var(--tbw-focus-outline);
        outline-offset: var(--tbw-focus-outline-offset);
      }
    }

    /* ========== Sticky Columns ========== */

    .sticky-left,
    .sticky-right {
      position: sticky;
      z-index: 25;
    }

    .sticky-left {
      box-shadow: 1px 0 0 var(--tbw-color-border);
    }

    .sticky-right {
      box-shadow: -1px 0 0 var(--tbw-color-border);
    }
  }

  /* ========== Size popover (WCAG 2.2 SC 2.5.7) ==========
     Click-only alternative to dragging a resize handle — shared by core column
     resizing and the shell's tool-panel splitter. Mounted inside the grid host
     (light DOM) so theme tokens cascade in; the top layer keeps it clear of
     ancestor overflow. Fallbacks stay for the `document.body` path used when no
     host is available. Lives here rather than in a plugin stylesheet so plugins
     that reuse the popover need no CSS of their own. */
  .tbw-size-popover {
    position: fixed;
    inset: auto;
    margin: 0;
    padding: var(--tbw-spacing-xs, 0.25rem);
    display: flex;
    align-items: center;
    gap: var(--tbw-spacing-xs, 0.25rem);
    background: var(--tbw-color-panel-bg, light-dark(#ffffff, #232528));
    color: var(--tbw-color-fg, light-dark(#1a1a1a, #f0f0f0));
    border: 1px solid var(--tbw-color-border, light-dark(#d0d0d4, #454545));
    border-radius: var(--tbw-border-radius, 0.25rem);
    box-shadow: 0 4px 12px var(--tbw-color-shadow, rgba(0, 0, 0, 0.3));
    font-family: var(--tbw-font-family, system-ui, sans-serif);
    font-size: var(--tbw-font-size-sm, 0.9285em);
    line-height: 1;
    z-index: 10001;

    /* Themes set radii for row-height controls (Material uses 12px), which turns
       a ~26px control into a lozenge and clips its content. Cap the inner radius
       while the container keeps the theme's own value. */
    --tbw-size-popover-radius: min(var(--tbw-border-radius, 0.25rem), 6px);

    &[hidden] {
      display: none;
    }

    button,
    input {
      min-height: var(--tbw-touch-target-min, 26px);
      border-radius: var(--tbw-size-popover-radius);
      color: inherit;
      font: inherit;
      line-height: 1;

      &:focus-visible {
        outline: var(--tbw-focus-outline, 2px solid var(--tbw-color-accent, #4a90d9));
        outline-offset: var(--tbw-focus-outline-offset, 1px);
      }
    }

    button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: var(--tbw-touch-target-min, 26px);
      padding: 0 var(--tbw-spacing-sm, 0.5rem);
      background: transparent;
      border: 1px solid transparent;
      cursor: pointer;

      /* Border-only hover disappears in flat and high-contrast themes, so tint
         the surface as well. */
      &:hover {
        background: var(--tbw-color-row-hover, light-dark(#f0f0f0, #2a2a2a));
        border-color: var(--tbw-color-border-strong, light-dark(#777777, #666666));
      }

      &:active {
        background: var(--tbw-color-accent, #4a90d9);
        color: var(--tbw-color-accent-fg, #ffffff);
      }
    }

    input {
      width: 5ch;
      padding: 0 var(--tbw-spacing-xs, 0.25rem);
      background: var(--tbw-color-bg, light-dark(#ffffff, #1a1c20));
      text-align: center;
      border: 1px solid var(--tbw-color-border, light-dark(#d0d0d4, #454545));

      /* The − / + buttons already do what the native spinner does, and the UA
         widget overlaps the value at this size and ignores the theme. */
      appearance: textfield;

      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        appearance: none;
        margin: 0;
      }
    }
  }
}
